import { lazy, Suspense, useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Outlet, Navigate } from "react-router-dom";
import Layout from "./components/layout/layout";
import "../src/components/styles/globals.css";
import "../src/components/styles/rtl.css";
import AuthPage from "./pages/AuthPage.js";
import SocialCallback from "./pages/SocialCallback.js";
import { PROFILE_INCOMPLETE_KEY } from "./api/profileCompletion";

// Protected Route wrapper component
const ProtectedRoute = () => {
  const token = localStorage.getItem('auth_token');
  if (!token) {
    return <Navigate to="/" replace />;
  }
  // After a social sign-in: the required profile details come first
  if (localStorage.getItem(PROFILE_INCOMPLETE_KEY) === '1' && window.location.pathname !== '/complete-profile') {
    return <Navigate to="/complete-profile" replace />;
  }
  return <Outlet />;
};

const DeleteAccount = lazy(() => import("./pages/DeleteAccountPage.js"));
const Dashboard = lazy(() => import("./pages/DashboardPage.js"));
const ResetPassword = lazy(() => import("./pages/ResetPassword.js"));
const Speakersummary = lazy(() => import("./pages/Speakersummary.js"));
const SpeakerManagement = lazy(() => import("./pages/SpeakerManagementPage.js"));
const Publications = lazy(() => import("./pages/Publications.js"));
const Support = lazy(() => import("./pages/SupportPage.js"));
const Profile = lazy(() => import("./pages/ProfilePage.js"));
const Invoices = lazy(() => import("./pages/InvoicesPage.js"));
const Network = lazy(() => import("./pages/NetworkPage.js"));
const Letters = lazy(() => import("./pages/LettersPage.js"));
const Submissions = lazy(() => import("./pages/SubmissionsPage.js"));
const NewSubmission = lazy(() => import("./pages/NewSubmission.js"));
const Packages = lazy(() => import("./pages/PackagesPage.js"));
const Purchases = lazy(() => import("./pages/PurchasesPage.js"));
const AuthorServices = lazy(() => import("./pages/AuthorServicesPage.js"));
const BuyAuthorService = lazy(() => import("./pages/BuyAuthorServicePage.js"));
const EventDetails = lazy(() => import("./context/EventDetails.js"));
const CheckoutPage = lazy(() => import("./pages/CheckoutPage.js"));
const TicketDetail = lazy(() => import("./components/Purchases/TicketDetail.js"));
const NotificationsPage = lazy(() => import("./pages/NotificationsPage.js"));
const SocialWall = lazy(() => import("./pages/SocialWallPage.js"));
const ErrorPage = lazy(() => import("./pages/ErrorPage.js"));
const CompleteProfile = lazy(() => import("./pages/CompleteProfile.js"));

const PageLoader = () => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      height: "50vh",
      flexDirection: "column",
      gap: "16px",
    }}
  >
    <div
      style={{
        width: "40px",
        height: "40px",
        border: "3px solid #E6F1FB",
        borderTopColor: "#185FA5",
        borderRadius: "50%",
        animation: "spin 0.8s linear infinite",
      }}
    />
    <span style={{ fontSize: "13px", color: "#888" }}>Loading...</span>
  </div>
);

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // ✅ تعيين عنوان افتراضي
    document.title = 'Emirates Scholar Center Portal';
    console.log('📄 [App] Page title set to default');

    // Load user data from localStorage
    const storedData = localStorage.getItem("login_response_full");
    if (storedData) {
      try {
        const parsedData = JSON.parse(storedData);
        
        const isUnverified = parsedData?.userVerified === false || 
                             parsedData?.userVerified === 'false' || 
                             parsedData?.userVerified === 0 || 
                             parsedData?.success?.userVerified === false || 
                             parsedData?.Profile?.userVerified === false || 
                             parsedData?.UserDetails?.userVerified === false ||
                             parsedData?.Profile?.usr_det?.userVerified === false ||
                             parsedData?.user_verified === false || 
                             parsedData?.success?.user_verified === false || 
                             parsedData?.Profile?.user_verified === false;

        if (isUnverified) {
          const currentPath = window.location.pathname;
          if (currentPath !== '/verify-email') {
            window.location.href = '/verify-email';
            return;
          }
        }
        
        const profileName = parsedData?.Profile?.name || '';
        const profileLname = parsedData?.Profile?.lname || '';
        const fullName = [profileName, profileLname].filter(Boolean).join(' ') || 'Unknown User';
        const userData = {
          name: fullName,
          esid: parsedData?.UserDetails?.membership || "ESID-XXXX XXXX XXXX",
          avatar: parsedData?.Profile?.usr_det?.imageLink || "",
          email: parsedData?.Profile?.email || "",
          role: parsedData?.Profile?.usr_det?.profession || parsedData?.Profile?.usr_det?.title || "",
        };
        
        setUser(userData);
      } catch (error) {
        console.error("Error parsing user data from localStorage:", error);
        setUser(null);
      }
    }
    setLoading(false);
  }, []);

  if (loading) {
    return <PageLoader />;
  }

  return (
    <BrowserRouter>
      <Routes>
        {/* Event details page - outside Layout */}
        <Route path="/event-details" element={
          <Suspense fallback={<PageLoader />}>
            <EventDetails />
          </Suspense>
        } />

        <Route index element={<AuthPage />} />
        <Route path="/" element={<AuthPage />} />
        <Route path="/verify-email" element={<AuthPage />} />
        {/* Returning from LinkedIn / Google / Apple sign-in */}
        <Route path="/auth/social" element={<SocialCallback />} />
        
        {/* Protected Routes */}
        <Route element={<ProtectedRoute />}>
          {/* Required details after a social sign-in - outside Layout but protected */}
          <Route path="/complete-profile" element={
            <Suspense fallback={<PageLoader />}>
              <CompleteProfile />
            </Suspense>
          } />

          {/* Stripe checkout page - outside Layout but protected */}
          <Route path="/checkout" element={
            <Suspense fallback={<PageLoader />}>
              <CheckoutPage />
            </Suspense>
          } />

          {/* Layout wraps all authenticated pages */}
          <Route
            path="/"
            element={
              <Layout user={user}>
                <Suspense fallback={<PageLoader />}>
                  <Outlet />
                </Suspense>
              </Layout>
            }
          >
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/DeleteAccount" element={<DeleteAccount />} />
            <Route path="/ResetPassword" element={<ResetPassword />} />
            <Route path="/Speakersummary" element={<Speakersummary />} />
            <Route path="/speakermanagement" element={<SpeakerManagement />} />
            <Route path="/Publications" element={<Publications />} />
            <Route path="/support" element={<Support />} />
            <Route path="/profile" element={<Profile />} />
            <Route path="/invoices" element={<Invoices />} />
            <Route path="/network" element={<Network />} />
            <Route path="/letters" element={<Letters />} />
            <Route path="/submissions" element={<Submissions />} />
            <Route path="/new-submission" element={<NewSubmission />} />
            <Route path="/packages" element={<Packages />} />
            <Route path="/purchases" element={<Purchases />} />
            <Route path="/author-services" element={<AuthorServices />} />
            <Route path="/buy-author-service" element={<BuyAuthorService />} />
            <Route path="/notifications" element={<NotificationsPage />} />
            <Route path="/social-wall" element={<SocialWall />} />
            <Route path="/ticket/:ticketId" element={<TicketDetail />} />
          </Route>
        </Route>

        {/* Global Error and Wildcard Routes */}
        <Route path="/error" element={
          <Suspense fallback={<PageLoader />}>
            <ErrorPage />
          </Suspense>
        } />
        <Route path="*" element={
          <Suspense fallback={<PageLoader />}>
            <ErrorPage code="404" />
          </Suspense>
        } />
      </Routes>
    </BrowserRouter>
  );
}

export default App;